<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>

@if (isLoading()) {
  <p-progressBar mode="indeterminate"></p-progressBar>
}

<app-page-shell>
  <app-page-hero eyebrow="Administration" title="Developer Platform" subtitle="Tenant-owned API credentials, scoped access, rate limits, webhooks, usage logs, and a sandbox-first playground.">
    <div nav>
      @for (tab of visibleTabs(); track tab.value) {
        <button
          pButton
          type="button"
          [label]="tab.label"
          [icon]="tab.icon"
          [outlined]="activeTab() !== tab.value"
          [severity]="activeTab() === tab.value ? 'primary' : 'secondary'"
          (click)="setActiveTab(tab.value)">
        </button>
      }
    </div>
    <div actions>
      <a pButton [href]="openApiUrl" target="_blank" rel="noopener" label="OpenAPI" icon="pi pi-external-link" [outlined]="true" severity="secondary"></a>
      <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="load()"></p-button>
      <p-button label="New Credential" icon="pi pi-plus" (onClick)="openClientDialog()" [disabled]="!canManageCredentials"></p-button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

  @if (oneTimeSecret()) {
    <app-page-data-panel title="One-time Secret" subtitle="This secret is shown once. Store it before closing this panel.">
      <div class="dp-secret-panel">
        <div>
          <strong>{{ oneTimeSecret()?.title }}</strong>
          <code>{{ oneTimeSecret()?.secret }}</code>
        </div>
        <div class="dp-inline-actions">
          <p-button icon="pi pi-copy" label="Copy" [outlined]="true" severity="secondary" (onClick)="copyText(oneTimeSecret()?.secret || '', 'Secret copied')"></p-button>
          <p-button icon="pi pi-times" label="Dismiss" [outlined]="true" severity="secondary" (onClick)="oneTimeSecret.set(null)"></p-button>
        </div>
      </div>
    </app-page-data-panel>
  }

  @if (activeTab() === 'dashboard') {
    <div class="dp-grid dp-grid--two">
      <app-page-data-panel title="Rate Plans" subtitle="Default quotas are enforced per credential and environment.">
        <p-table [value]="ratePlans()" styleClass="p-datatable-sm" responsiveLayout="scroll">
          <ng-template pTemplate="header">
            <tr>
              <th>Plan</th>
              <th>Environment</th>
              <th>Minute</th>
              <th>Day</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-plan>
            <tr>
              <td><strong>{{ plan.label }}</strong></td>
              <td><p-tag [value]="plan.environment" [severity]="environmentSeverity(plan.environment)"></p-tag></td>
              <td>{{ plan.requestsPerMinute | number }}/min</td>
              <td>{{ plan.requestsPerDay | number }}/day</td>
            </tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>

      <app-page-data-panel title="Available Scopes" subtitle="Sensitive finance, payroll, and HR-style resources are excluded from v1.">
        <div class="dp-scope-list">
          @for (scope of scopes(); track scope.code) {
            <div class="dp-scope-row">
              <div>
                <strong>{{ scope.label }}</strong>
                <span>{{ scope.code }}</span>
              </div>
              <p-tag [value]="scope.module" severity="info"></p-tag>
            </div>
          }
        </div>
      </app-page-data-panel>
    </div>

    @if (canViewUsage) {
      <app-page-data-panel title="Recent Usage" subtitle="Latest external API calls across active credentials." [resultText]="usageLogs().length + ' calls'">
        <ng-container *ngTemplateOutlet="usageTable"></ng-container>
      </app-page-data-panel>
    }
  }

  @if (activeTab() === 'credentials') {
    <app-page-data-panel title="API Credentials" subtitle="Client secrets are hashed server-side and never shown again after creation or rotation." [resultText]="clients().length + ' credentials'">
      <p-table [value]="clients()" [loading]="isLoading()" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '980px' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Name</th>
            <th>Client ID</th>
            <th>Environment</th>
            <th>Rate Plan</th>
            <th>Scopes</th>
            <th>Status</th>
            <th>Last Used</th>
            <th class="text-right">Manage</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-client>
          <tr>
            <td>
              <div class="dp-stack">
                <strong>{{ client.clientName }}</strong>
                <small>{{ client.description || 'No description' }}</small>
              </div>
            </td>
            <td><code>{{ client.clientId }}</code></td>
            <td><p-tag [value]="client.environment" [severity]="environmentSeverity(client.environment)"></p-tag></td>
            <td>{{ planLabel(client.ratePlan, client.environment) }}</td>
            <td>
              <div class="dp-chip-list">
                @for (scope of client.scopes || []; track scope) {
                  <span>{{ scope }}</span>
                }
              </div>
            </td>
            <td><p-tag [value]="client.status" [severity]="statusSeverity(client.status)"></p-tag></td>
            <td>{{ client.lastUsedAt ? (client.lastUsedAt | date:'short') : '-' }}</td>
            <td class="text-right">
              <div class="dp-row-actions">
                <p-button icon="pi pi-pencil" [text]="true" pTooltip="Edit credential" (onClick)="openClientDialog(client)" [disabled]="!canManageCredentials"></p-button>
                <p-button icon="pi pi-key" [text]="true" severity="secondary" pTooltip="Rotate secret" (onClick)="rotateClientSecret(client)" [disabled]="!canManageCredentials || client.status === 'REVOKED'"></p-button>
                <p-button icon="pi pi-ban" [text]="true" severity="danger" pTooltip="Revoke credential" (onClick)="revokeClient(client)" [disabled]="!canManageCredentials || client.status === 'REVOKED'"></p-button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="8" class="text-center p-4">No API credentials have been created.</td>
          </tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  }

  @if (activeTab() === 'usage' && canViewUsage) {
    <app-page-data-panel title="Usage Logs" subtitle="Request IDs, status codes, and rate-limit outcomes for external API traffic." [resultText]="usageLogs().length + ' rows'">
      <ng-container *ngTemplateOutlet="usageTable"></ng-container>
    </app-page-data-panel>
  }

  @if (activeTab() === 'webhooks') {
    <app-page-data-panel title="Webhook Subscriptions" subtitle="Business events are signed with HMAC secrets and delivered to tenant-managed endpoints." [resultText]="webhooks().length + ' subscriptions'">
      <div class="dp-panel-toolbar">
        <p-button label="New Webhook" icon="pi pi-plus" (onClick)="openWebhookDialog()" [disabled]="!canManageWebhooks"></p-button>
      </div>
      <p-table [value]="webhooks()" [loading]="isLoading()" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '840px' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Name</th>
            <th>Event</th>
            <th>Target</th>
            <th>Status</th>
            <th>Failures</th>
            <th class="text-right">Manage</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-webhook>
          <tr>
            <td>
              <div class="dp-stack">
                <strong>{{ webhook.name }}</strong>
                <small>Secret {{ webhook.secretPrefix || '-' }}</small>
              </div>
            </td>
            <td><code>{{ webhook.eventCode }}</code></td>
            <td class="dp-url-cell">{{ webhook.targetUrl }}</td>
            <td><p-tag [value]="webhook.active ? 'ACTIVE' : 'DISABLED'" [severity]="webhook.active ? 'success' : 'secondary'"></p-tag></td>
            <td>{{ webhook.failureCount || 0 }}</td>
            <td class="text-right">
              <div class="dp-row-actions">
                <p-button icon="pi pi-pencil" [text]="true" pTooltip="Edit webhook" (onClick)="openWebhookDialog(webhook)" [disabled]="!canManageWebhooks"></p-button>
                <p-button icon="pi pi-key" [text]="true" severity="secondary" pTooltip="Rotate signing secret" (onClick)="rotateWebhookSecret(webhook)" [disabled]="!canManageWebhooks"></p-button>
                <p-button icon="pi pi-trash" [text]="true" severity="danger" pTooltip="Delete webhook" (onClick)="deleteWebhook(webhook)" [disabled]="!canManageWebhooks"></p-button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="text-center p-4">No webhook subscriptions configured.</td>
          </tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  }

  @if (activeTab() === 'reference') {
    <app-page-data-panel title="API Reference" subtitle="Swagger-style operation reference generated from the live LogiLink Enterprise OpenAPI document.">
      <div actions>
        <a pButton [href]="openApiUrl" target="_blank" rel="noopener" label="Open JSON" icon="pi pi-external-link" [outlined]="true" severity="secondary"></a>
      </div>
      <app-api-reference [specUrl]="openApiUrl" [currentServerUrl]="apiRootUrl"></app-api-reference>
    </app-page-data-panel>
  }

  @if (activeTab() === 'playground') {
    <div class="dp-grid dp-grid--playground">
      <app-page-data-panel title="Request Builder" subtitle="Sandbox is the default execution environment. Production requires confirmation.">
        <div class="dp-form-grid">
          <label>
            <span>Environment</span>
            <p-select [options]="environmentOptions" [(ngModel)]="playground.environment" optionLabel="label" optionValue="value"></p-select>
          </label>
          <label>
            <span>Credential</span>
            <p-select [options]="playgroundCredentialOptions()" [(ngModel)]="selectedPlaygroundClientId" optionLabel="label" optionValue="value" [showClear]="true" placeholder="Manual or saved client" (onChange)="selectPlaygroundClient()"></p-select>
          </label>
          <label>
            <span>Client ID</span>
            <input pInputText [(ngModel)]="playground.clientId" placeholder="ll_...">
          </label>
          <label>
            <span>Client Secret</span>
            <input pInputText type="password" [(ngModel)]="playground.clientSecret" placeholder="Paste one-time secret">
          </label>
          <label>
            <span>Endpoint</span>
            <p-select [options]="endpointOptions" [(ngModel)]="selectedEndpointPath" optionLabel="label" optionValue="value" (onChange)="selectEndpoint()"></p-select>
          </label>
          <label>
            <span>Method</span>
            <p-select [options]="methodOptions" [(ngModel)]="playground.method" optionLabel="label" optionValue="value"></p-select>
          </label>
        </div>

        <label class="dp-textarea-field">
          <span>Path</span>
          <input pInputText [(ngModel)]="playground.path" placeholder="/shipments?page=0&pageSize=50">
        </label>

        @if (playground.method !== 'GET') {
          <label class="dp-textarea-field">
            <span>JSON Body</span>
            <textarea pTextarea rows="7" [(ngModel)]="playground.body" placeholder='{"referenceNumber":"..."}'></textarea>
          </label>
        }

        <div class="dp-play-actions">
          <p-button label="Run Request" icon="pi pi-play" (onClick)="executePlayground()" [loading]="playgroundLoading()" [disabled]="!canUsePlayground"></p-button>
          <p-button label="Copy cURL" icon="pi pi-copy" [outlined]="true" severity="secondary" (onClick)="copyText(generatedCurl(), 'cURL copied')"></p-button>
        </div>
      </app-page-data-panel>

      <app-page-data-panel title="OAuth Token Flow" subtitle="The playground first exchanges client credentials for a short-lived JWT, then calls /api/external/v1.">
        <pre class="dp-code-block">{{ generatedCurl() }}</pre>
      </app-page-data-panel>
    </div>

    <app-page-data-panel title="Live Response" subtitle="Response headers include request and rate-limit details when returned by the API.">
      @if (playgroundResult()) {
        <div class="dp-response-summary">
          <p-tag [value]="'HTTP ' + playgroundResult()?.status" [severity]="responseSeverity(playgroundResult()?.status || 0)"></p-tag>
          <span>{{ playgroundResult()?.elapsedMs }} ms</span>
          <span>Request ID {{ playgroundResult()?.headers?.['x-request-id'] || '-' }}</span>
          <span>Remaining {{ playgroundResult()?.headers?.['x-ratelimit-remaining'] || '-' }}</span>
          <span>Reset {{ playgroundResult()?.headers?.['x-ratelimit-reset'] || '-' }}</span>
        </div>
        <pre class="dp-code-block">{{ playgroundResult() | json }}</pre>
      } @else {
        <div class="dp-empty-state">Run a sandbox request to inspect the external API response envelope and rate-limit headers.</div>
      }
    </app-page-data-panel>
  }

  <ng-template #usageTable>
    <p-table [value]="usageLogs()" [loading]="isLoading()" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '900px' }">
      <ng-template pTemplate="header">
        <tr>
          <th>Time</th>
          <th>Credential</th>
          <th>Environment</th>
          <th>Request</th>
          <th>Status</th>
          <th>Duration</th>
          <th>Request ID</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-log>
        <tr>
          <td>{{ log.createdAt ? (log.createdAt | date:'short') : '-' }}</td>
          <td>{{ log.clientName || log.clientId }}</td>
          <td><p-tag [value]="log.environment" [severity]="environmentSeverity(log.environment)"></p-tag></td>
          <td><code>{{ log.method }} {{ log.path }}</code></td>
          <td><p-tag [value]="log.statusCode" [severity]="responseSeverity(log.statusCode)"></p-tag></td>
          <td>{{ log.durationMs || 0 }} ms</td>
          <td><code>{{ log.requestId || '-' }}</code></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7" class="text-center p-4">No external API usage has been logged.</td>
        </tr>
      </ng-template>
    </p-table>
  </ng-template>
</app-page-shell>

<p-dialog
  [header]="clientForm.apiClientId ? 'Edit API Credential' : 'New API Credential'"
  [(visible)]="clientDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(880px, 96vw)' }">
  <div class="dp-form-grid">
    <label>
      <span>Name</span>
      <input pInputText [(ngModel)]="clientForm.clientName" placeholder="Sandbox integration">
    </label>
    <label>
      <span>Environment</span>
      <p-select [options]="environmentOptions" [(ngModel)]="clientForm.environment" optionLabel="label" optionValue="value" [disabled]="!!clientForm.apiClientId" (onChange)="onClientEnvironmentChange()"></p-select>
    </label>
    <label>
      <span>Rate Plan</span>
      <p-select [options]="clientRatePlanOptions()" [(ngModel)]="clientForm.ratePlan" optionLabel="label" optionValue="value"></p-select>
    </label>
    <label>
      <span>Allowed Origins</span>
      <input pInputText [(ngModel)]="clientForm.allowedOrigins" placeholder="https://client.example.com">
    </label>
  </div>
  <label class="dp-textarea-field">
    <span>Description</span>
    <textarea pTextarea rows="3" [(ngModel)]="clientForm.description" placeholder="Purpose and owner"></textarea>
  </label>
  <label class="dp-textarea-field">
    <span>Scopes</span>
    <p-multiselect [options]="scopeOptions" [(ngModel)]="clientForm.scopes" optionLabel="label" optionValue="value" display="chip" placeholder="Choose scopes"></p-multiselect>
  </label>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" icon="pi pi-times" [outlined]="true" severity="secondary" (onClick)="clientDialogVisible = false"></p-button>
    <p-button label="Save Credential" icon="pi pi-check" (onClick)="saveClient()" [disabled]="!canManageCredentials"></p-button>
  </ng-template>
</p-dialog>

<p-dialog
  [header]="webhookForm.webhookSubscriptionId ? 'Edit Webhook' : 'New Webhook'"
  [(visible)]="webhookDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(760px, 96vw)' }">
  <div class="dp-form-grid dp-form-grid--webhook">
    <label>
      <span>Name</span>
      <input pInputText [(ngModel)]="webhookForm.name" placeholder="Shipment status callback">
    </label>
    <label>
      <span>Event Code</span>
      <input pInputText [(ngModel)]="webhookForm.eventCode" placeholder="SHIPMENT_DELIVERY_CONFIRMED">
    </label>
    <label class="dp-switch-line">
      <p-toggleswitch [(ngModel)]="webhookForm.active"></p-toggleswitch>
      <span>Active</span>
    </label>
  </div>
  <label class="dp-textarea-field">
    <span>Target URL</span>
    <input pInputText [(ngModel)]="webhookForm.targetUrl" placeholder="https://client.example.com/logilink/events">
  </label>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" icon="pi pi-times" [outlined]="true" severity="secondary" (onClick)="webhookDialogVisible = false"></p-button>
    <p-button label="Save Webhook" icon="pi pi-check" (onClick)="saveWebhook()" [disabled]="!canManageWebhooks"></p-button>
  </ng-template>
</p-dialog>
